import type {
  BankTransferView,
  OrderPaymentSummary as OrderPayment,
  TaxInvoiceView,
} from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { PAYMENT_STATUS_LABELS } from '@/entities/payment';
import { formatDateTime, formatKrw } from '@/shared/lib';
import { OrderBankTransferRows } from './order-bank-transfer-rows';
import { OrderTaxInvoiceRow } from './order-tax-invoice-row';

interface OrderPaymentSummaryProps {
  readonly payment: OrderPayment;
  readonly bankTransfer: BankTransferView | null;
  readonly taxInvoice: TaxInvoiceView;
  /** 보여 줄 영수증 주소 — 위젯이 안전한 https 이고 보여 줄 상태일 때만 넘긴다. */
  readonly receiptUrl: string | null;
}

const ROW_CLASS = 'flex gap-4 py-3';
const TERM_CLASS = 'w-24 shrink-0 text-muted-foreground sm:w-28';

/** 결제 정보 — 내 견적 상세의 결제 정보(QuotePaymentSummary)와 같은 모양. 상태·금액·수단·승인 일시·영수증. */
export const OrderPaymentSummary = ({
  payment,
  receiptUrl,
  bankTransfer,
  taxInvoice,
}: OrderPaymentSummaryProps): ReactElement => (
  <section aria-label="결제 정보" className="flex flex-col gap-3">
    <p
      aria-hidden
      className="text-[11px] leading-none font-semibold tracking-[0.18em] text-muted-foreground uppercase"
    >
      Payment
    </p>
    <dl className="divide-y divide-foreground/10 border-y border-foreground/10 text-sm">
      {bankTransfer ? (
        <OrderBankTransferRows transfer={bankTransfer} />
      ) : (
        <div className={ROW_CLASS}>
          <dt className={TERM_CLASS}>결제 상태</dt>
          <dd className="font-semibold">{PAYMENT_STATUS_LABELS[payment.status]}</dd>
        </div>
      )}
      <div className={ROW_CLASS}>
        <dt className={TERM_CLASS}>결제 금액</dt>
        <dd className="tabular-nums">{formatKrw(payment.amount)}</dd>
      </div>
      {bankTransfer !== null || payment.method !== null ? (
        <div className={ROW_CLASS}>
          <dt className={TERM_CLASS}>결제 수단</dt>
          <dd>{bankTransfer !== null ? '계좌이체' : payment.method}</dd>
        </div>
      ) : null}
      {payment.approvedAt !== null ? (
        <div className={ROW_CLASS}>
          <dt className={TERM_CLASS}>승인 일시</dt>
          <dd className="tabular-nums">{formatDateTime(payment.approvedAt)}</dd>
        </div>
      ) : null}
      <OrderTaxInvoiceRow view={taxInvoice} />
    </dl>
    {receiptUrl !== null ? (
      <a
        href={receiptUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="w-fit text-sm font-semibold underline underline-offset-4 hover:no-underline"
      >
        영수증 보기
      </a>
    ) : null}
  </section>
);
